import { useCallback } from "react";
import { SizeMe } from "react-sizeme";

import "./MultiPartSlider.scss";
import { MultiPartSliderHandle } from "./components/MultiPartSliderHandle";

export type MultiPartSliderOption = {
  label: string;
  color: string;
  value: number;
};

type Props = {
  width?: number | null;
  max: number;
  min: number;
  minGap?: number;
  values: MultiPartSliderOption[];
  setValues(values: MultiPartSliderOption[]): void;
  finishedSettingValues(): void;
};

export const MultiPartSlider = (props: Props) => {
  return <SizeMe monitorWidth>{({ size }) => <MultiPartSliderComponent {...{ ...props, width: size.width }} />}</SizeMe>;
};

export const MultiPartSliderComponent = (props: Props) => {
  const { values, setValues } = props;
  const trackWidth = props.width ? props.width - 28 : 0;
  const valueRange = props.max - props.min;
  const scale = trackWidth / valueRange;

  const updateValuesForSlide = useCallback(
    (index: number, delta: number, left: number) => {
      let valuesToUpdate = [...values];
      const gap = props.minGap || 10;

      if (left < gap) {
        return;
      }
      if (index > 0 && left < gap + valuesToUpdate[index - 1].value * scale) {
        return;
      }

      if (index !== valuesToUpdate.length - 1 && left > props.max * scale - gap) {
        return;
      }

      valuesToUpdate[index].value = Math.round(left * (valueRange / trackWidth));
      setValues(valuesToUpdate);
    },
    [values]
  );

  let segments = [];
  let handles = [];
  for (let i = 0; i < values.length; i++) {
    const item = values[i];

    const left = i === 0 ? 0 : values[i - 1].value * scale;
    const itemWidth = i === values.length - 1 ? props.max * scale - left : item.value * scale - left;

    segments.push(
      <div
        key={i}
        className="segment"
        style={{
          background: item.color,
          width: itemWidth,
          left: left + "px",
          zIndex: values.length - i
        }}
      />
    );

    if (i !== values.length - 1) {
      handles.push(
        <MultiPartSliderHandle
          key={i}
          index={i}
          item={item}
          left={item.value * scale}
          updateValuesForSlide={updateValuesForSlide}
          finishedSettingValues={props.finishedSettingValues}
        />
      );
    }
  }

  return (
    <div className="MultiPartSlider" style={{ width: props.width + "px" }}>
      <div className="labels">
        {values.map((item, index) => (
          <div className="labelItem" key={item.label} style={{ color: item.color }}>
            {item.label} ({index === values.length - 1 ? values[values.length - 2].value + 1 + "+" : item.value})
          </div>
        ))}
      </div>
      <div className="slider" style={{ width: props.width + "px" }}>
        <div className="track" style={{ width: trackWidth + "px" }}>
          {segments}
          {handles}
        </div>
      </div>
    </div>
  );
};
